import {
  FormLabel,
} from "@mui/material";
import { Tooltip } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import './ICVActivityStyles.css';
import { Button } from "reactstrap";
import { useDispatch, useSelector } from "react-redux";
import { FormUtils } from "../../../utils/FormUtils";
import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { icvGetCropBySearchAPICall, onholdQCApi, updateQCFeedback } from "../../../api/icvActivityAPI";
import { LoggedUserType } from "../../../models/GlobalModels";
import { loginSelector } from "../../../redux/slices/loginSlice";
import { CROP_SOWN_OPTIONS, ICV_STATUS, IMAGE_QUALITY_OPTIONS, ON_HOLD_OPTIONS } from "../../../utils/Constant";
import { CustomRadioGroup, CustomTextField } from "../../../components/CustomFormComponents";
import MultiSelectDropdown from "../../../components/MultiSelectDropdown";

type FormValues = {
  imageQuality: string;
  imageQualityRemarks: string;
  cropSown: string;
  cropSownRemarks: string;
  additionalRemarks: string;
  cropName: string[];
  onHold: string;
  onHoldRemarks: string | null;
};

interface ICVActivityQCFormProps {
  formData: any;
  selectedStatus: "Reassigned" | "Completed" | 'On_Hold' | null;
  setSelectedStatus: (v: "Reassigned" | "Completed" | 'On_Hold' | null) => void;
  hideButtons?: boolean;
  setIsFormValid: (v: boolean) => void;
  setIsSubmitting: (v: boolean) => void;
  onHoldChange?: (value: string) => void;
}

const ICVActivityQCForm = forwardRef<any, ICVActivityQCFormProps>(
  (
    {
      formData,
      selectedStatus,
      setSelectedStatus,
      hideButtons = false,
      setIsFormValid,
      setIsSubmitting,
      onHoldChange,
    },
    ref
  ) => {
    const user: LoggedUserType = useSelector(loginSelector).user;
    const [formDisabled, setFormDisabled] = useState<boolean>(false);
    const [selectedCropNames, setSelectedCropNames] = useState<string[]>([]);
    const dispatch: any = useDispatch();
    const { storeICVGetCropsMastersData, storeICVStatusUpdateRes, storeICVOnHoldRes } = useSelector((state: any) => state.icvActivity);

    const cropMastersData = FormUtils.convertDataList(
      storeICVGetCropsMastersData?.data ?? []
    );

    const {
      control,
      handleSubmit,
      watch,
      setValue,
      getValues,
      formState: { errors, isValid },
    } = useForm<FormValues>({
      defaultValues: {
        imageQuality: "",
        imageQualityRemarks: "",
        cropSown: "",
        onHold: "",
        cropSownRemarks: "",
        additionalRemarks: "",
        cropName: [],
        onHoldRemarks: null,
      },
      mode: "all",
    });
    const imageQuality = watch("imageQuality");
    const cropSown = watch("cropSown");
    const onHold = watch("onHold");

    const onSubmit = (data: FormValues) => {
      if (!isValid) return;

      setIsSubmitting(true);
      const cropNameArray = selectedCropNames;
      if (selectedStatus === 'On_Hold') {
        dispatch(onholdQCApi([formData?.icvId], ICV_STATUS.ON_HOLD, data?.onHoldRemarks, user?.emailId))
      }
      else {
        dispatch(
          updateQCFeedback(
            formData?.icvId,
            selectedStatus === "Reassigned"
              ? ICV_STATUS.REASSIGNED
              : selectedStatus === "Completed"
                ? ICV_STATUS.COMPLETED
                : null,
            formData?.qcMailId,
            user?.employeeName,
            user?.emailId,
            data?.imageQuality === "acceptable"
              ? "Acceptable"
              : data?.imageQuality === "nonAcceptable"
                ? "Non Acceptable"
                : null,
            data?.imageQualityRemarks,
            data?.cropSown === "yes" ? "Yes" : data?.cropSown === "no" ? "No" : null,
            data?.cropSownRemarks,
            cropNameArray,
            data?.additionalRemarks,
          )
        );
      }
    };

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        handleSubmit(onSubmit)();
      },
    }));

    useEffect(() => {
      setIsFormValid(isValid);
    }, [isValid, setIsFormValid]);

    useEffect(() => {
      if (formData?.icvStatusId !== ICV_STATUS.QC_IN_PROGRESS) {
        setValue('onHold', formData?.icvStatusId === ICV_STATUS.ON_HOLD ? 'yes' : 'no');
        if(formData?.icvStatusId === ICV_STATUS.ON_HOLD) {
          setValue('onHoldRemarks', formData?.icvStatusId === ICV_STATUS.ON_HOLD ? formData?.qcHoldRemarks : '');
        } else {
          setValue('imageQuality', formData?.imageQualityCheckByQc === 'Acceptable' ? 'acceptable' : formData?.imageQualityCheckByQc === 'Non Acceptable' ? 'nonAcceptable' : '');
          setValue('cropSown', formData?.cropSownCheckByQc === 'Yes' ? 'yes' : formData?.cropSownCheckByQc === 'No' ? 'no' : '');
          setValue('imageQualityRemarks', formData?.imageQualityRemarksByQc);
          setValue('cropSownRemarks', formData?.cropSownRemarksByQc);
          setValue('cropName', []);
          setValue('additionalRemarks', formData?.additionalRemarksByQc);

          if (formData?.cropNameByQc) {
            let cropNames: string[] = [];
            if (Array.isArray(formData.cropNameByQc)) {
              cropNames = formData.cropNameByQc;
            } else if (typeof formData.cropNameByQc === 'string') {
              cropNames = formData.cropNameByQc.split(',').map((name: string) => name.trim()).filter(Boolean);
            }
            setSelectedCropNames(cropNames);
            setValue('cropName', cropNames);
          } else {
            setSelectedCropNames([]);
            setValue('cropName', []);
          }
        }
      }
      if (
        formData?.icvStatusId === ICV_STATUS.QC_IN_PROGRESS &&
        user?.permissions?.includes('Quality-Check-feedback')
      ) {
        setValue('onHold', formData?.icvStatusId === ICV_STATUS.ON_HOLD ? 'yes' : 'no');
        setFormDisabled(false);
      }
      else {
        setFormDisabled(true);
      }
      dispatch(icvGetCropBySearchAPICall("", formData?.stateId, formData?.districtId))
    }, []);

    useEffect(() => {
      if (
        storeICVStatusUpdateRes &&
        !(
          storeICVStatusUpdateRes?.statusCode === 1 &&
          storeICVStatusUpdateRes?.data === 1
        )
      ) {
        setIsSubmitting(false);
      }
    }, [storeICVStatusUpdateRes, setIsSubmitting]);


    useEffect(() => {
      if (
        storeICVOnHoldRes &&
        !(
          storeICVOnHoldRes?.statusCode === 1
        )
      ) {
        setIsSubmitting(false);
      }
    }, [storeICVOnHoldRes, setIsSubmitting]);

    const handleCropNameChange = (selectedCrops: any[]) => {
      setSelectedCropNames(selectedCrops);
      setValue("cropName", selectedCrops, { shouldValidate: true });
    };

    return (
      <div className="qc-form-section">
        <form onSubmit={handleSubmit(onSubmit)}>
          {/* On Hold */}
          <div className="crop-sown">
            <CustomRadioGroup
              name="onHold"
              control={control}
              label="On Hold?"
              options={ON_HOLD_OPTIONS}
              disabled={formDisabled}
              onChange={(value: string) => {
                if (value === "yes") {
                  setValue("imageQuality", "");
                  setValue("imageQualityRemarks", "");
                  setValue("cropSown", "");
                  setSelectedCropNames([]);
                  setValue("cropSownRemarks", "");
                  setValue("additionalRemarks", "");
                } else if (value === "no") {
                  setValue("onHoldRemarks", "");
                }
                onHoldChange && onHoldChange(value);
              }}
              error={errors.onHold}
            />

            {onHold === "yes" && (
              <div>
                <div>
                  <p>QC questions are hidden because this case is marked as On Hold.</p>
                </div>
                <CustomTextField
                  name="onHoldRemarks"
                  control={control}
                  label="Remarks"
                  required={true}
                  disabled={formDisabled}
                  rules={{required: "Remarks for On Hold are requried"}}
                />
              </div>
            )}
            {onHold === "no" && (
              <>
                <div className="qc-form-section">
                  <div>
                    {/* Image Quality */}
                    <div className="image-quality w-100">
                      <CustomRadioGroup
                        name="imageQuality"
                        control={control}
                        label="Image Quality"
                        options={IMAGE_QUALITY_OPTIONS}
                        required={true}
                        disabled={formDisabled}
                        rules={{ required: "Image quality is required" }}
                        onChange={(value: string) => {
                          if (value === "acceptable") {
                            setValue("imageQualityRemarks", "", { shouldValidate: true });
                          }
                        }}
                        error={errors.imageQuality}
                      />

                      {imageQuality === "nonAcceptable" && (
                        <CustomTextField
                          name="imageQualityRemarks"
                          control={control}
                          label="Remarks"
                          required={true}
                          disabled={formDisabled}
                          rules={{ required: "Image quality remarks are required" }}
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Crop Sown */}
                <div className="crop-sown">
                  <CustomRadioGroup
                    name="cropSown"
                    control={control}
                    label="Crop Sown"
                    options={CROP_SOWN_OPTIONS}
                    required={true}
                    disabled={formDisabled}
                    rules={{ required: "Crop sown is required" }}
                    onChange={(value: string) => {
                      if (value === "yes") {
                        setValue("cropSownRemarks", "");
                      } else if (value === "no") {
                        setSelectedCropNames([]);
                      }
                    }}
                    error={errors.cropSown}
                  />

                  {cropSown === "no" && (
                    <div>
                      <CustomTextField
                        name="cropSownRemarks"
                        control={control}
                        label="Remarks"
                        required={true}
                        disabled={formDisabled}
                        rules={{ required: "Crop sown remarks are required" }}
                      />
                    </div>
                  )}

                  {cropSown === "yes" && (
                    <div className="crop-name-row">
                      <span className="crop-name-label">
                        Crop Name:
                        <span className="required">*</span>
                      </span>
                      {formDisabled ? (
                        <div className="crop-name-display">
                          <Tooltip
                            title={
                              selectedCropNames.length > 0
                                ? selectedCropNames.join(", ")
                                : "N/A"
                            }
                            placement="bottom-start"
                            componentsProps={{
                              tooltip: {
                                sx: {
                                  bgcolor: "black",
                                  color: "white",
                                  fontSize: "12px",
                                  "& .MuiTooltip-arrow": {
                                    color: "black",
                                  },
                                },
                              },
                            }}
                          >
                            <span className="crop-name-value">
                              {selectedCropNames.length > 0
                                ? selectedCropNames.join(", ")
                                : "N/A"}
                            </span>
                          </Tooltip>
                        </div>
                      ) : (
                        <div className="crop-name-input-container">
                          <Controller
                            name="cropName"
                            control={control}
                            rules={{ required: "Crop name is required" }}
                            render={({ field, fieldState: { error } }) => (
                              <div style={{ width: "100%" }}>
                                <MultiSelectDropdown
                                  options={cropMastersData}
                                  label=""
                                  value={selectedCropNames}
                                  onChange={handleCropNameChange}
                                  storeLabels={true}
                                  showSelectAll={false}
                                  showClearAll={true}
                                  optional={false}
                                />
                                {error && (
                                  <span
                                    style={{
                                      color: "#dc3545",
                                      fontSize: "12px",
                                      marginTop: "4px",
                                      display: "block",
                                    }}
                                  >
                                    {error.message}
                                  </span>
                                )}
                              </div>
                            )}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Additional Remarks */}
                <div className="additional-remarks mt-2">
                  <p></p>
                  <FormLabel component="legend" sx={{ fontSize: "14px" }}>
                    Additional Remarks
                  </FormLabel>
                  <CustomTextField
                    name="additionalRemarks"
                    control={control}
                    label=""
                    disabled={formDisabled}
                    width="100%"
                  />
                </div>
              </>
            )}
          </div>
        </form>
      </div>
    );
  }
);

export default ICVActivityQCForm;